import { zodResolver } from '@hookform/resolvers/zod'
import type { BackofficeAuthSignInWithEmailData } from '@repo/sdk/gen'
import { backofficeAuthSignInWithEmail } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { InputPassword } from '@repo/ui/commons/input-password'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import logoBrush from '@/assets/logo-brush.png'
import logoWordmark from '@/assets/logo-wordmark.png'
import i18n, { pageTitle, translateApp, useTranslation } from '@/lib/i18n'
import { fetchFreshSession } from '@/lib/query-options'

const LoginSchema = z.object({
	email: z.email({ error: () => i18n.t('auth:validation.email_invalid') }),
	password: z
		.string()
		.min(1, { error: () => i18n.t('auth:validation.password_invalid') })
})

export const Route = createFileRoute('/(auth)/login')({
	validateSearch: z.object({
		redirect: z.string().startsWith('/').optional()
	}),
	component: AdminLogin,
	head: () => ({
		meta: [{ title: pageTitle(translateApp('it', 'auth:log_in')) }]
	})
})

function AdminLogin() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const router = useRouter()
	const queryClient = useQueryClient()
	const { redirect } = Route.useSearch()

	const form = useForm<z.infer<typeof LoginSchema>>({
		resolver: zodResolver(LoginSchema),
		defaultValues: { email: '', password: '' }
	})

	const signIn = useMutation({
		mutationFn: (body: BackofficeAuthSignInWithEmailData['body']) =>
			backofficeAuthSignInWithEmail({ body }),
		onError: () => form.resetField('password'),
		onSuccess: async () => {
			await fetchFreshSession(queryClient)
			// The destination loads before the navigation, so it opens ready
			// instead of flashing its loader. A failed preload only costs that:
			// navigation loads the route again.
			await router
				.preloadRoute({ to: redirect ?? '/' })
				.catch(() => undefined)
			await navigate({ to: redirect ?? '/' })
		}
	})

	return (
		<AuthCard>
			<AuthCardHeader
				title={
					<span className='relative mx-auto block w-44'>
						<img
							alt='Mantegna Libri'
							className='h-auto w-full'
							height={646}
							src={logoBrush}
							width={987}
						/>
						<img
							alt=''
							className='absolute inset-0 h-auto w-full'
							height={646}
							src={logoWordmark}
							width={987}
						/>
					</span>
				}
			/>
			<AuthCardBody>
				<form
					onSubmit={form.handleSubmit((data) => signIn.mutate(data))}
				>
					<FieldGroup>
						{signIn.isError && (
							<AuthErrorAlert
								onClose={() => signIn.reset()}
								title={formatError(signIn.error)}
							/>
						)}
						<Controller
							control={form.control}
							name='email'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('common:email')}
									</FieldLabel>
									<Input
										{...field}
										aria-invalid={fieldState.invalid}
										id={field.name}
										placeholder={t(
											'auth:email_placeholder'
										)}
										type='email'
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<Controller
							control={form.control}
							name='password'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<div className='flex items-center justify-between'>
										<FieldLabel htmlFor={field.name}>
											{t('common:password')}
										</FieldLabel>
										<ButtonLink
											size='xs'
											to='/forgot-password'
											variant='link'
										>
											{t('auth:login.forgot_password')}
										</ButtonLink>
									</div>
									<InputPassword
										{...field}
										aria-invalid={fieldState.invalid}
										id={field.name}
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<Field>
							<Button disabled={signIn.isPending} type='submit'>
								{signIn.isPending ? (
									<Spinner />
								) : (
									t('auth:log_in')
								)}
							</Button>
						</Field>
					</FieldGroup>
				</form>
			</AuthCardBody>
		</AuthCard>
	)
}
